import { CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon } from "@heroicons/react/24/outline"
import { CalendarDaysIcon, ClockIcon, MapPinIcon, VideoCameraIcon } from "@heroicons/react/24/solid"
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api"
import dayjs from "dayjs"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"
import React, { useCallback, useEffect, useState } from "react"
import { twMerge } from "tailwind-merge"

import { Divider } from "../components/shared/Layout"
import GraphQLProvider from "../providers/GraphQLProvider"
import { formatPhone } from "../utils/utils"

dayjs.extend(utc)
dayjs.extend(timezone)

// Helper component for detail items with icons
const DetailItem = ({ icon: Icon, children, iconClassName = "h-5 w-5", textClassName = "text-sm" }) => (
  <div className="flex items-start space-x-3">
    <Icon className={twMerge("shrink-0", iconClassName)} aria-hidden="true" />
    <span className={twMerge(textClassName)}>{children}</span>
  </div>
)

// New Modal Component
const CancelConfirmationModal = ({ isOpen, onClose, onConfirm, accentColor }) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 transition-opacity duration-300 ease-in-out">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl transition-all duration-300 ease-in-out sm:my-8 sm:scale-100">
        <div className="flex items-start">
          <div className="mr-4 flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-light sm:h-10 sm:w-10">
            <ExclamationTriangleIcon className="h-6 w-6 text-red" aria-hidden="true" />
          </div>
          <div className="mt-0 text-left">
            <h3 className="text-lg font-semibold leading-6 text-black" id="modal-title">
              Cancel Appointment
            </h3>
            <div className="mt-2">
              <p className="text-sm text-gray">
                Are you sure you want to cancel your registration for this appointment? This action cannot be undone.
              </p>
            </div>
          </div>
        </div>
        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            className="inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-80 sm:ml-3 sm:w-auto"
            style={{ backgroundColor: accentColor }}
            onClick={onConfirm}>
            Yes, cancel
          </button>
          <button
            type="button"
            className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-black shadow-sm ring-1 ring-inset ring-gray-light hover:bg-gray-ultralight sm:mt-0 sm:w-auto"
            onClick={onClose}>
            Go back
          </button>
        </div>
      </div>
    </div>
  )
}

const PageContent = ({ eventRegistration }) => {
  const { event, eventType, practice, client, registration, isOwnerCancelled, token } = eventRegistration

  // Define theme colors
  const theme = practice?.theme || {}
  const accentColor = theme.colorAccent || "#DF0054"

  const linkStyle = {
    color: accentColor
  }

  // Using client.email if available, otherwise fallback as before
  const clientEmail = client?.email || practice.user.email || "[email address]"

  const imageUrl = event.photoUrl || event.imageUrl

  // Format dates
  const timeZone = event.timeZone || "UTC"
  const startDate = dayjs(event.startsAt).tz(timeZone)
  const endDate = dayjs(event.endsAt).tz(timeZone)
  const duration = endDate.diff(startDate, "minute")

  // Format location
  const isVirtual = event.kind === "virtual"
  const locationDisplay = isVirtual ? (event.location ? event.location : "Link will be emailed to you") : event.location

  // Google Maps state
  const [mapCenter, setMapCenter] = useState(null)
  const [markerPosition, setMarkerPosition] = useState(null)
  const [geocodingError, setGeocodingError] = useState(false)
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)

  const initialIsGeneralCancelled = registration?.state === "cancelled"

  const [cancellationStatus, setCancellationStatus] = useState({
    message: "",
    isError: false,
    isCancelled: initialIsGeneralCancelled
  })

  const geocodeLocation = useCallback(() => {
    if (!event.location || !window.google || !isMapLoaded) return

    const geocoder = new window.google.maps.Geocoder()
    geocoder.geocode({ address: event.location }, (results, status) => {
      if (status === "OK" && results[0]) {
        const position = {
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng()
        }
        setMapCenter(position)
        setMarkerPosition(position)
        setGeocodingError(false)
      } else {
        setGeocodingError(true)
      }
    })
  }, [event.location, isMapLoaded])

  useEffect(() => {
    if (!isVirtual) {
      geocodeLocation()
    }
  }, [geocodeLocation, isVirtual])

  const onMapLoad = useCallback(() => {
    setIsMapLoaded(true)
  }, [])

  const mapContainerStyle = {
    width: "100%",
    height: "144px" // h-36
  }

  const mapOptions = {
    disableDefaultUI: true,
    zoomControl: true,
    zoom: 14 // Match static map zoom
  }

  const handleOpenCancelModal = (e) => {
    e.preventDefault()
    if (cancellationStatus.isCancelled) return
    setCancellationStatus({ message: "", isError: false, isCancelled: false }) // Reset message before opening
    setIsCancelModalOpen(true)
  }

  const handleCloseCancelModal = () => {
    setIsCancelModalOpen(false)
  }

  const handleConfirmCancel = async () => {
    if (!token) {
      setCancellationStatus({
        message: "Error: Registration token not found.",
        isError: true,
        isCancelled: cancellationStatus.isCancelled
      })
      setIsCancelModalOpen(false)
      return
    }

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(`/manage_event_registrations/${token}/cancel`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(csrfToken && { "X-CSRF-Token": csrfToken })
        }
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setCancellationStatus({
          message: data.message || "Your appointment has been cancelled.",
          isError: false,
          isCancelled: true
        })
      } else {
        setCancellationStatus({
          message: data.error || "Failed to cancel appointment. Please try again.",
          isError: true,
          isCancelled: cancellationStatus.isCancelled
        })
      }
    } catch (error) {
      setCancellationStatus({
        message: "An unexpected error occurred. Please try again.",
        isError: true,
        isCancelled: cancellationStatus.isCancelled
      })
    } finally {
      setIsCancelModalOpen(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-ultralight">
      {/* Top Header - Practice Name */}
      <header className="bg-white shadow-sm">
        <div className="mx-auto max-w-5xl p-4 lg:px-8 sm:px-6">
          <h1 className="text-xl font-semibold">{practice.user.name}</h1>
        </div>
      </header>

      <main className="py-10">
        <div className="mx-auto max-w-2xl px-4 lg:px-8 sm:px-6">
          {/* Confirmation/Status Message based on cancellation state */}
          <div className="mb-8 text-center">
            {isOwnerCancelled ? (
              <>
                <ExclamationTriangleIcon className="mx-auto h-16 w-16" />
                <h2 className="mt-4 text-3xl font-bold tracking-tight">Registration Cancelled</h2>
                <p className="mt-2 text-sm">Your registration for this event has already been cancelled.</p>
                <div className="mt-6">
                  <p className="mt-2 text-sm">If you want, you can always </p>
                  <a
                    href={`${practice.url}/${eventType.slug}`}
                    className="inline-block w-auto rounded-md px-4 py-2 text-center text-white"
                    style={{ backgroundColor: accentColor }}>
                    Go back to the event page
                  </a>
                </div>
                <p className="mt-2 text-sm">
                  If you have any questions, please contact the practice at {practice.user.phone}.
                </p>
              </>
            ) : cancellationStatus.isCancelled ? (
              <>
                <InformationCircleIcon className="mx-auto h-16 w-16" />
                <h2 className="mt-4 text-3xl font-bold tracking-tight">Appointment Status</h2>
                <p className="mt-2 text-sm">
                  {cancellationStatus.message && !cancellationStatus.isError
                    ? cancellationStatus.message
                    : "This appointment registration is cancelled."}
                </p>
                <p className="mt-2 text-sm">
                  If you have any questions, please contact the practice at {practice.user.phone}.
                </p>
              </>
            ) : (
              <>
                <CheckCircleIcon className="mx-auto h-16 w-16" style={{ color: accentColor }} />
                <h2 className="mt-4 text-3xl font-bold tracking-tight">Your appointment is booked!</h2>
                <p className="mt-2 text-sm">
                  An email confirmation has been sent to <span className="font-bold">{clientEmail}</span>.
                </p>
              </>
            )}
          </div>

          {/* Main Content Card: Only show if not the owner's specific cancelled view */}
          {!isOwnerCancelled && (
            <div className="mx-auto max-w-xl overflow-hidden rounded-lg bg-white p-8 shadow">
              <div className="px-0">
                <div className="mt-6 space-y-4">
                  <div className="flex items-start space-x-4">
                    {imageUrl && <img src={imageUrl} alt={event.name} className="h-24 w-24 rounded-xl" />}
                    <h3 className="left-6 pt-4 text-2xl font-semibold leading-7">{event.name}</h3>
                  </div>
                  <DetailItem icon={CalendarDaysIcon}>
                    <p className="font-bold">{startDate.format("dddd, MMMM D, YYYY")}</p>
                  </DetailItem>
                  <DetailItem icon={ClockIcon}>
                    <p className="font-bold">{duration} min</p>
                    <p className="top-4 text-sm text-gray-dark">
                      {startDate.format("h:mm A")} - {endDate.format("h:mm A")} ({timeZone})
                    </p>
                  </DetailItem>
                  {isVirtual ? (
                    <DetailItem icon={VideoCameraIcon}>
                      <p className="font-bold">Virtual</p>
                      {event.location ? (
                        <p className="top-4 text-sm text-gray-dark">
                          <a
                            href={event.location}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:opacity-80"
                            style={linkStyle}>
                            {event.location}
                          </a>
                        </p>
                      ) : (
                        <p className="top-4 text-sm text-gray-dark">{locationDisplay}</p>
                      )}
                    </DetailItem>
                  ) : (
                    <DetailItem icon={MapPinIcon}>
                      <p className="font-bold">In-person</p>
                      <p className="top-4 text-sm text-gray-dark">{event.location}</p>
                    </DetailItem>
                  )}
                </div>

                {/* Map Section for In-Person Events */}
                {!isVirtual && (
                  <div className="mx-auto mt-6 w-full max-w-map-container">
                    <LoadScript googleMapsApiKey={process.env.GOOGLE_MAPS_API_KEY} onLoad={onMapLoad}>
                      {mapCenter && !geocodingError ? (
                        <GoogleMap mapContainerStyle={mapContainerStyle} center={mapCenter} options={mapOptions}>
                          {markerPosition && <Marker position={markerPosition} title={event.location} />}
                        </GoogleMap>
                      ) : geocodingError ? (
                        <div className="flex h-36 w-full items-center justify-center rounded-md bg-gray-light">
                          <p className="text-sm text-gray-dark">Unable to load map for: {event.location}</p>
                        </div>
                      ) : (
                        <div className="flex h-36 w-full items-center justify-center rounded-md bg-gray-light">
                          <p className="text-sm text-gray-dark">Loading map...</p>
                        </div>
                      )}
                    </LoadScript>
                  </div>
                )}
              </div>

              <Divider />
              {/* Practitioner Info - Styled Separately */}
              <div className="px-0">
                <div className="flex items-center space-x-4">
                  <img className="h-12 w-12 rounded-xl" src={practice.user.defaultPhotoUrl} alt={practice.user.name} />
                  <div>
                    <p className="text-lg font-medium">{practice.user.name}</p>
                    {practice.user.phone && (
                      <p className="text-sm" style={linkStyle}>
                        {formatPhone(practice.user.phone)}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <Divider />
              {/* Action Links */}
              <div className="mt-8 flex flex-col items-start space-y-3 sm:flex-row sm:justify-center sm:space-x-4 sm:space-y-0">
                {cancellationStatus.isCancelled ? (
                  <p className="text-sm font-semibold text-green">Appointment Cancelled</p>
                ) : (
                  false && ( // TODO: Uncomment this when we have a cancel button
                    <a
                      href="#"
                      onClick={handleOpenCancelModal}
                      className="text-sm font-semibold hover:opacity-80"
                      style={linkStyle}>
                      Cancel appointment
                    </a>
                  )
                )}
                <a href={practice.url} className="text-sm font-semibold hover:opacity-80" style={linkStyle}>
                  Book another appointment
                </a>
              </div>
              {/* API Message Area: For errors or detailed success from API post-action */}
              {cancellationStatus.message && !isCancelModalOpen && (
                <div className={`mt-4 text-center text-sm ${cancellationStatus.isError ? "text-red" : "text-green"}`}>
                  {cancellationStatus.message}
                </div>
              )}
            </div>
          )}

          {/* App Store Section Placeholder */}
          <div className="mt-16 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray">
              <img src="/images/mobile-app-img.png" alt="Heal.me" />
            </div>
            <p className="text-lg font-semibold">Book your next appointment in 1-click</p>
            <p className="mt-1 text-sm">Download the Heal.me mobile app</p>
            <div className="mt-4 flex justify-center space-x-3">
              {/* Placeholder for App Store buttons */}
              <div className="flex h-10 w-32 items-center justify-center rounded-md bg-black text-white">
                <a href="https://apps.apple.com/app/id1599744549">
                  <img src="/images/app-store-badge.png" alt="App Store" className="h-10 w-32" />
                </a>
              </div>
              <div className="flex h-10 w-32 items-center justify-center rounded-md bg-black text-white">
                <a href="https://play.google.com/store/apps/details?id=me.heal.mobile">
                  <img src="/images/playstore.png" alt="Google Play" className="h-10 w-32" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Powered by Heal.me Footer */}
      <footer className="mt-auto bg-gray-ultralight py-8 text-center">
        <div className="flex items-center justify-center">
          <a href="/">
            <img src="/images/HealMeLogo.png" alt="Heal.me" className="h-10 w-10 rounded-lg" />
          </a>
          <div className="p-3 font-manrope text-sm font-semibold">POWERED BY HEAL.ME</div>
        </div>
      </footer>
      <CancelConfirmationModal
        isOpen={isCancelModalOpen}
        onClose={handleCloseCancelModal}
        onConfirm={handleConfirmCancel}
        accentColor={accentColor}
      />
    </div>
  )
}

export default function ManageEventRegistration(props) {
  return (
    <GraphQLProvider>
      <PageContent {...props} />
    </GraphQLProvider>
  )
}
